import { FC, useState } from 'react'
import { MyPagination } from 'src/components/ui'

import { useDebounce } from '@hooks/useDebounce'
import { Skeleton } from '@mui/material'
import { useSelector } from 'react-redux'
import { EmptySection, PropertyCard } from 'src/components/elements'
import { AnnouncementModal } from 'src/components/elements/modals'
import { IProperty } from 'src/interfaces/IProperty'
import { useGetPropertiesQuery } from 'src/redux/api'
import { RootState } from 'src/redux/store'
import { useTranslation } from 'react-i18next'
import ErrorState from 'src/components/elements/error-state/ErrorState'

interface ITabAllProperties {
  propertySearchId: string
  onlyFavorites?: boolean
}

const PropertiesTab: FC<ITabAllProperties> = ({ propertySearchId, onlyFavorites }) => {
  const { t } = useTranslation()
  const { search, sort } = useSelector((state: RootState) => state.propertySearch)
  const debouncedSearch = useDebounce(search, 500)

  const [currentPage, setCurrentPage] = useState(1)
  const [openModal, setOpenModal] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState<IProperty | null>(null)

  const {
    data: { properties, totalPages } = { properties: [] },
    isLoading,
    isError,
    refetch,
  } = useGetPropertiesQuery(
    { propertySearchId, search: debouncedSearch, page: currentPage, sort, limit: 8, onlyFavorites },
    { refetchOnMountOrArgChange: true },
  )

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleOpenModal = (property: IProperty) => {
    setSelectedProperty(property)
    setOpenModal(true)
  }

  if (isError) {
    return <ErrorState onRetry={refetch} />
  }

  return (
    <div className="flex flex-col gap-4">
      {!isLoading && properties.length === 0 && (
        // the way back to the listings, like the page's own empty state
        <EmptySection
          title={t('emptyWishlist.title')}
          description={t('emptyWishlist.description')}
          buttonText={t('emptyWishlist.buttonText')}
          buttonLink="/"
          buttonKind="go"
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <Skeleton variant="rounded" height={397} />
            <Skeleton variant="rounded" height={397} />
            <Skeleton variant="rounded" height={397} />
            <Skeleton variant="rounded" height={397} />
          </>
        ) : (
          properties.map((property) => (
            <PropertyCard
              onClick={() => handleOpenModal(property)}
              key={property.id}
              property={property}
            />
          ))
        )}

        {selectedProperty && (
          <AnnouncementModal
            open={openModal}
            property={selectedProperty}
            handleClose={() => setOpenModal(false)}
          />
        )}
      </div>
      <MyPagination
        totalPages={totalPages || 0}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

export default PropertiesTab
